import type { SVGProps } from "react";
import {
  Activity,
  ArrowLeftRight,
  AudioLines,
  Bell,
  BookOpen,
  Calendar,
  Camera,
  CaseSensitive,
  ChartColumn,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  CircleX,
  Clock,
  Code,
  CreditCard,
  Crop,
  Download,
  FileText,
  Globe,
  IdCard,
  Image,
  Inbox,
  Info,
  Keyboard,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  MessageSquare,
  MessageSquareText,
  Mic,
  Moon,
  Paperclip,
  Pause,
  Pen,
  Phone,
  Pin,
  Play,
  Plug,
  Plus,
  Puzzle,
  QrCode,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Search,
  SendHorizontal,
  Settings,
  SlidersHorizontal,
  Smile,
  Sparkles,
  SquarePen,
  Star,
  Sticker,
  Sun,
  Tag,
  Trash2,
  Type,
  User,
  UserPlus,
  Users,
  Video,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from "lucide-react";

export type IconName =
  | "smile"
  | "paperclip"
  | "sparkles"
  | "inbox"
  | "sun"
  | "moon"
  | "user"
  | "user-plus"
  | "lock"
  | "id"
  | "activity"
  | "keyboard"
  | "settings"
  | "card"
  | "logout"
  | "plus"
  | "phone"
  | "video"
  | "search"
  | "trash"
  | "check"
  | "check-double"
  | "chevron-left"
  | "chevron-right"
  | "menu"
  | "users"
  | "tag"
  | "sliders"
  | "zap"
  | "message"
  | "puzzle"
  | "file-text"
  | "code"
  | "clock"
  | "globe"
  | "message-circle"
  | "whatsapp"
  | "qr"
  | "sms"
  | "mail"
  | "api"
  | "telegram"
  | "line"
  | "pin"
  | "star"
  | "book"
  | "x"
  | "play"
  | "pause"
  | "mic"
  | "send"
  | "transfer"
  | "image"
  | "audio"
  | "document"
  | "camera"
  | "poll"
  | "calendar"
  | "sticker"
  | "rotate-left"
  | "rotate-right"
  | "zoom-in"
  | "zoom-out"
  | "refresh"
  | "pen"
  | "type"
  | "crop"
  | "close-circle"
  | "pen-square"
  | "text-aa"
  | "crop-box"
  | "rotate-ccw"
  | "rotate-cw"
  | "zoom-in-bold"
  | "zoom-out-bold"
  | "trash-bin"
  | "reset-circle"
  | "download"
  | "info"
  | "bell";

type IconProps = {
  name: IconName;
  size?: number;
  className?: string;
};

// Marcas não existem na biblioteca: seguem com o desenho próprio.
const brandIcons: Record<string, (props: SVGProps<SVGSVGElement>) => JSX.Element> = {
  whatsapp: (props) => (
    <svg fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M6 19l.6-2.4A7.5 7.5 0 1112 19a7.4 7.4 0 01-3.4-.8L6 19z" />
      <path d="M9.5 9.5c.4 1 1.6 2.2 2.6 2.6l1.1-.6.9 1.5-1.6 1c-2.2-.2-4.8-2.8-5-5l1-1.6 1.5.9-.5 1.2z" />
    </svg>
  ),
  telegram: (props) => (
    <svg fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M21 4L3 11l6 2 2 6 3-4 4 3 3-14z" />
      <path d="M9 13l6-5" />
    </svg>
  ),
  line: (props) => (
    <svg fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M5 6h14a4 4 0 014 4v2a4 4 0 01-4 4H9l-4 3v-3H5a4 4 0 01-4-4V10a4 4 0 014-4z" />
      <path d="M8 11h1M11 11h1M14 11h1" />
    </svg>
  ),
};

const Icons: Record<Exclude<IconName, "whatsapp" | "telegram" | "line">, LucideIcon> = {
  smile: Smile,
  paperclip: Paperclip,
  sparkles: Sparkles,
  inbox: Inbox,
  sun: Sun,
  moon: Moon,
  user: User,
  "user-plus": UserPlus,
  lock: Lock,
  id: IdCard,
  activity: Activity,
  keyboard: Keyboard,
  settings: Settings,
  card: CreditCard,
  logout: LogOut,
  plus: Plus,
  phone: Phone,
  video: Video,
  search: Search,
  trash: Trash2,
  check: Check,
  "check-double": CheckCheck,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  menu: Menu,
  users: Users,
  tag: Tag,
  sliders: SlidersHorizontal,
  zap: Zap,
  message: MessageSquare,
  puzzle: Puzzle,
  "file-text": FileText,
  code: Code,
  clock: Clock,
  globe: Globe,
  "message-circle": MessageCircle,
  qr: QrCode,
  sms: MessageSquareText,
  mail: Mail,
  api: Plug,
  pin: Pin,
  star: Star,
  book: BookOpen,
  x: X,
  play: Play,
  pause: Pause,
  mic: Mic,
  send: SendHorizontal,
  transfer: ArrowLeftRight,
  image: Image,
  audio: AudioLines,
  document: FileText,
  camera: Camera,
  poll: ChartColumn,
  calendar: Calendar,
  sticker: Sticker,
  "rotate-left": RotateCcw,
  "rotate-right": RotateCw,
  "zoom-in": ZoomIn,
  "zoom-out": ZoomOut,
  refresh: RefreshCw,
  pen: Pen,
  type: Type,
  crop: Crop,
  "close-circle": CircleX,
  "pen-square": SquarePen,
  "text-aa": CaseSensitive,
  "crop-box": Crop,
  "rotate-ccw": RotateCcw,
  "rotate-cw": RotateCw,
  "zoom-in-bold": ZoomIn,
  "zoom-out-bold": ZoomOut,
  "trash-bin": Trash2,
  "reset-circle": RotateCcw,
  download: Download,
  info: Info,
  bell: Bell,
};

export function Icon({ name, size = 18, className }: IconProps) {
  const Brand = brandIcons[name];
  if (Brand) return <Brand width={size} height={size} className={className} aria-hidden="true" />;
  const Svg = Icons[name as keyof typeof Icons];
  if (!Svg) {
    return null;
  }
  return <Svg size={size} strokeWidth={1.75} className={className} aria-hidden="true" />;
}
